import { clientAccess } from './clientAccess';
import { clientService } from './clientService';
import { activityService } from './activityService';
import { clientMetadataService } from './clientMetadataService';
import { billingFor, type Billing } from '@/config/contract';
import type { CreditConfig } from '@/types';

/** A billing-admin roster entry (CLIENT#/BILLING_ADMIN#<sub>): the grant, held until the account goes. */
export interface BillingAdminRow {
  sub: string;
  email: string | null;
  grantedBy: string | null;
  grantedAt: string | null;
}

/** A person whose member row on the client's roles table names the Billing role. */
export interface BillingRoleHolder {
  sub: string;
  updatedAt: string | null;
  updatedBy: string | null;
}

/** A client Cognito user (for the promote picker). */
export interface ClientUser {
  sub: string;
  email: string;
  enabled: boolean;
}

/** A top-up-balance event-log entry (CLIENT#/TXN#…). `credits` is signed. */
export interface CreditTxn {
  kind: 'topup' | 'settlement' | 'adjustment' | string;
  credits: number;
  month?: string;
  createdAt: string;
  createdBy?: string;
  note?: string;
}

/** Live credit standing derived from a client's ledger (read-only, cross-account). */
export interface CreditStanding {
  /** Live top-up balance: settled TXN events minus not-yet-settled monthly overflow. Can be negative. */
  availableBalance: number;
  /** Σ of all TXN credits (top-ups + month-close settlements + adjustments). */
  settledBalance: number;
  /** Overflow past allocation for months not yet settled (open month + any closed-but-unsettled). */
  liveOverflow: number;
  /**
   * Per NZ billing month ('YYYY-MM') → credits used, allocation in effect, whether settled, plus the
   * underlying USD economics (Arcanum-internal): `costUsd` = real token+AgentCore consumption that
   * month, `revenueUsd` = credit value billed. margin ≈ revenueUsd / costUsd.
   */
  months: Record<string, { used: number; allocation: number; settled: boolean; costUsd: number; revenueUsd: number }>;
  /** Top-up-balance event log, newest first (for audit / display). */
  txns: CreditTxn[];
  /** Billing-admin roster (same partition read — no extra query). */
  billingAdmins: BillingAdminRow[];
  /** The client's pushed ledger CONFIG monthlyAllocations, if any (what the meter reads). */
  ledgerAllocations: number[] | null;
}

/**
 * CreditsService — central authoring for the Numa Credit System (SPK-015).
 *
 * Pricing config + top-ups are owned here (the portal), NOT in the client account. On save we:
 *   1. write the config to the central `numa-client-config` (source of truth), and
 *   2. PUSH it into the client account's `numa-<client>-credit-ledger` CONFIG row (through the
 *      client access Lambda, `credits.putConfig`) — which the in-client `credit-debit` Lambda
 *      reads at meter time.
 * The client's read path is unchanged; it never authors config.
 *
 * Two kinds of person hold Billing in a workspace: the grant on the ledger (written here and by
 * the in-client roster route, held until the account goes) and the Billing role on a member row
 * of `numa-<client>-roles` (given on the client's Users page, never written by the portal). The
 * portal reads the roles table to show the second kind beside the first. See
 * documentation/credits/09-billing-admin.md.
 */

type LedgerItem = Record<string, unknown>;

/**
 * lib/credit-pricing defaults — keep in sync with credit_pricing/credits.py + tiers.py.
 * The type leaves `synergyRates` out because the defaults hold none: see the note at the end.
 * A default for it here would be written into every client's credit config on the next save.
 */
export const DEFAULT_CREDIT_CONFIG: Omit<Required<CreditConfig>, 'synergyRates'> = {
  creditUsd: 0.3, // ~NZD $0.50/credit @ FX 1.69 — the NZD-anchored default
  margin: 2.0, // scalar fallback (unclassified); marginsByTier below is the real defence
  agentcoreMult: 1.234,
  trivialConsumptionUsd: 0.01,
  // Fixed outgoing IP: AWS cost + 5% (fixed-egress-credit-accrual).
  fixedEgressMargin: 1.05,
  // Half-credit pricing steps (the floor rounds up to 0.5); agent stays cheaper than chat at every tier.
  valueTiers: {
    chat: { low: 1, medium: 2, high: 5, very_high: 8 },
    agent: { low: 0.5, medium: 1.5, high: 3, very_high: 5 },
  },
  marginsByTier: { low: 1.1, medium: 1.25, high: 1.4, very_high: 1.6 },
  // New-client starter plan: 500 credits/mo (≈ NZD $254). Portal edits override per client.
  // (Lowered 2000 → 500 in TASK-405; existing clients were explicitly pinned at 2000 first.)
  monthlyAllocations: Array.from({ length: 12 }, () => 500),
  // Numa Voice per-minute USD anchors (provisional — confirm with Asa). Mirrors
  // lib/credit-pricing/voice_pricing.py defaults; the non-LLM cost of a call.
  voiceRates: { telephonyPerMin: 0.04, transcribePerMin: 0.024, contactLensPerMin: 0.015 },
  // NOTE: synergyRates (crawler-era estimate knobs) is retired — Synergy sync
  // now bills at MEASURED cost (run LLM spend + self-reported lambda GB-s,
  // ×1.25) with no per-client rate card; see synergy-store-sync/credits.py.
  // The schema still tolerates the key so old records parse; the portal no
  // longer seeds or edits it (a re-save drops it from the record).
};

export class CreditsService {
  /**
   * The people whose member row on the client's roles table names the Billing role. Read only:
   * the portal never writes that table. A missing table (a stack deployed before it existed) is
   * nobody; anything else that stops the read is thrown so the panel can say the list is unknown
   * rather than empty.
   */
  async listBillingRoleHolders(clientName: string): Promise<BillingRoleHolder[]> {
    const { holders } = await clientAccess<{ holders: BillingRoleHolder[] }>('credits.billingRoleHolders', {
      clientName,
    });
    return holders;
  }

  /** Save pricing config centrally and push it into the client's ledger CONFIG row. */
  async saveConfig(clientName: string, config: CreditConfig): Promise<void> {
    // 1. Central source of truth.
    await clientService.updateClientConfig(clientName, { creditConfig: config });
    // 2. Push to the client account (read by the in-client credit-debit Lambda).
    await clientAccess('credits.putConfig', { clientName, config });
    await activityService.logActivity({
      type: 'config',
      action: 'updated',
      resourceType: 'client-credits',
      resourceId: clientName,
      details: { creditConfig: config },
      success: true,
    });
  }

  /**
   * Top up the client's persistent balance by appending a `topup` event to the ledger event log
   * (CLIENT#/TXN#…). Balance is the sum of TXN events — there's no decrementing scalar — so a top-up
   * is an append, never a mutation. Also logged to the portal activity table for the audit trail.
   */
  async topUp(clientName: string, credits: number): Promise<void> {
    await clientAccess('credits.appendTxn', { clientName, kind: 'topup', credits });
    await activityService.logActivity({
      type: 'config',
      action: 'topup',
      resourceType: 'client-credits',
      resourceId: clientName,
      details: { credits },
      success: true,
    });
  }

  /**
   * Append a signed `adjustment` event to the balance log — a manual correction or a reset-to-zero.
   * `credits` is signed (negative to draw down / zero the balance). Auditable, never a deletion.
   */
  async adjustBalance(clientName: string, credits: number, note: string): Promise<void> {
    await clientAccess('credits.appendTxn', { clientName, kind: 'adjustment', credits, note });
    await activityService.logActivity({
      type: 'config',
      action: 'updated',
      resourceType: 'client-credits',
      resourceId: clientName,
      details: { adjustment: credits, note },
      success: true,
    });
  }

  /**
   * Put a client on Credits, Legacy or Own account. Both records change: the
   * contract in the portal's own table (a partner and trial dates no longer
   * apply, so they go) and the billing in the client config, which the
   * client's own stack reads at its next deploy. This portal's screens read
   * both now.
   */
  async setContract(clientName: string, contract: Billing): Promise<void> {
    const contractType = billingFor(contract);
    await clientService.updateClientConfig(clientName, { contractType });
    await clientMetadataService.updateFields(clientName, {
      contract,
      partner: null,
      trialStartDate: null,
      trialEndDate: null,
    });
    await activityService.logActivity({
      type: 'config',
      action: 'updated',
      resourceType: 'client-credits',
      resourceId: clientName,
      details: { contractType },
      success: true,
    });
  }

  /**
   * Set whether the in-app credits view is visible to the client (`showCredits`). Writes the central
   * numa-client-config only; it's emitted to the client's config.json at DEPLOY time, so it takes
   * visible effect on the next deploy. Metering runs regardless of this flag.
   */
  async setVisibility(clientName: string, show: boolean): Promise<void> {
    await clientService.updateClientConfig(clientName, { showCredits: show });
    await activityService.logActivity({
      type: 'config',
      action: 'updated',
      resourceType: 'client-credits',
      resourceId: clientName,
      details: { showCredits: show },
      success: true,
    });
  }

  /**
   * Derive the client's live credit standing from its ledger in one partition query (read-only,
   * cross-account). Implements the Option-B drawdown waterfall:
   *   - per month: credits used (exact `creditsCharged`, or revenue ÷ creditUsd for older rows) vs the
   *     allocation in effect (the month's frozen `allocationSnapshot`, else the live CONFIG allocation);
   *   - `liveOverflow` = Σ over months WITHOUT a settlement TXN of max(0, used − allocation);
   *   - `settledBalance` = Σ TXN credits (top-ups + settlements + adjustments);
   *   - `availableBalance` = settledBalance − liveOverflow (can be negative = invoice signal).
   * Months already settled by the nightly job are excluded from liveOverflow (their overflow is in TXN).
   */
  async getStanding(clientName: string): Promise<CreditStanding> {
    const { items } = await clientAccess<{ items: LedgerItem[] }>('credits.ledger', { clientName });

    const cfg = items.find((it) => String(it.SK) === 'CONFIG');
    const allocations: number[] = Array.isArray(cfg?.monthlyAllocations)
      ? (cfg!.monthlyAllocations as unknown[]).map(Number)
      : [];
    const creditUsd = Number(cfg?.creditUsd ?? DEFAULT_CREDIT_CONFIG.creditUsd) || DEFAULT_CREDIT_CONFIG.creditUsd;

    const txns: CreditTxn[] = items
      .filter((it) => String(it.SK ?? '').startsWith('TXN#'))
      .map((it) => ({
        kind: String(it.txnKind ?? ''),
        credits: Number(it.credits ?? 0),
        month: it.month ? String(it.month) : undefined,
        createdAt: String(it.createdAt ?? ''),
        createdBy: it.createdBy ? String(it.createdBy) : undefined,
        note: it.note ? String(it.note) : undefined,
      }))
      .sort((a, b) => (a.createdAt < b.createdAt ? 1 : -1));
    const settledBalance = txns.reduce((s, t) => s + t.credits, 0);
    const settledMonths = new Set(txns.filter((t) => t.kind === 'settlement' && t.month).map((t) => t.month!));

    const months: CreditStanding['months'] = {};
    let liveOverflow = 0;
    for (const it of items) {
      const sk = String(it.SK ?? '');
      if (!sk.startsWith('MONTH#')) continue;
      const m = sk.slice('MONTH#'.length);
      const monthIdx = Number(m.slice(5, 7)) - 1;
      const used =
        it.creditsCharged != null
          ? Number(it.creditsCharged)
          : creditUsd
            ? Math.round(Number(it.creditRevenueUsd ?? 0) / creditUsd)
            : 0;
      const allocation = it.allocationSnapshot != null ? Number(it.allocationSnapshot) : (allocations[monthIdx] ?? 0);
      const settled = settledMonths.has(m);
      months[m] = {
        used,
        allocation,
        settled,
        costUsd: Number(it.consumptionCostUsd ?? 0),
        revenueUsd: Number(it.creditRevenueUsd ?? 0),
      };
      if (!settled) liveOverflow += Math.max(0, used - Math.max(0, allocation));
    }

    const billingAdmins: BillingAdminRow[] = items
      .filter((it) => String(it.SK ?? '').startsWith('BILLING_ADMIN#'))
      .map((it) => ({
        sub: String(it.sub ?? String(it.SK).slice('BILLING_ADMIN#'.length)),
        email: it.email ? String(it.email) : null,
        grantedBy: it.grantedBy ? String(it.grantedBy) : null,
        grantedAt: it.grantedAt ? String(it.grantedAt) : null,
      }));

    return {
      availableBalance: settledBalance - Math.max(0, liveOverflow),
      settledBalance,
      liveOverflow,
      months,
      txns,
      billingAdmins,
      ledgerAllocations: allocations.length === 12 ? allocations : null,
    };
  }

  // ── Billing admins (who may see credit data in-client) ─────────────────────────────────────────
  // Membership lives in the client ledger (NOT a Cognito group — that'd be self-grantable by any
  // admin). The portal seeds the first billing-admin per client; after that an existing billing-admin
  // propagates from in-client User Management. All writes here are Arcanum-side (assume-role) + logged.

  /** List the client's enabled Cognito users (for the promote picker). */
  async listClientUsers(clientName: string): Promise<ClientUser[]> {
    const { users } = await clientAccess<{ users: ClientUser[] }>('credits.clientUsers', { clientName });
    return users;
  }

  /** The current billing-admin roster for a client (CLIENT#/BILLING_ADMIN# rows). */
  async listBillingAdmins(clientName: string): Promise<BillingAdminRow[]> {
    const { items } = await clientAccess<{ items: LedgerItem[] }>('credits.billingAdmins', { clientName });
    return items.map((it) => ({
      sub: String(it.sub ?? String(it.SK).slice('BILLING_ADMIN#'.length)),
      email: it.email ? String(it.email) : null,
      grantedBy: it.grantedBy ? String(it.grantedBy) : null,
      grantedAt: it.grantedAt ? String(it.grantedAt) : null,
    }));
  }

  /**
   * Seed/grant a billing-admin (bootstrap). Writes the grant on the ledger through the client
   * access Lambda and logs it. The grant is the only thing written: the Billing role is given on the client's
   * Users page, and the roles table is never touched from here.
   */
  async promoteBillingAdmin(clientName: string, sub: string, email: string | null): Promise<void> {
    await clientAccess('credits.grantBillingAdmin', { clientName, sub, email });
    await activityService.logActivity({
      type: 'config',
      action: 'billing-admin-grant',
      resourceType: 'client-credits',
      resourceId: clientName,
      details: { metadata: { sub, email } },
      success: true,
    });
  }

  /** Revoke a billing-admin: the grant on the ledger goes; the Billing role, if held, stays. */
  async demoteBillingAdmin(clientName: string, sub: string): Promise<void> {
    await clientAccess('credits.revokeBillingAdmin', { clientName, sub });
    await activityService.logActivity({
      type: 'config',
      action: 'billing-admin-revoke',
      resourceType: 'client-credits',
      resourceId: clientName,
      details: { metadata: { sub } },
      success: true,
    });
  }
}

export const creditsService = new CreditsService();
